/**
 * Project Switcher Component
 * Replaces WorkspaceSwitcher with dynamic API-loaded projects
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import { Building2, ShoppingCart, Landmark, Briefcase, Folder, Plus, ChevronDown, ChevronUp, Settings2, Trash2 } from 'lucide-react';
import { apiClient } from '../services/apiClient';

export interface Project {
  id: string;
  legacy_project_id: string | null;
  name: string;
  description: string | null;
  status: string;
  client_id: string;
  client_name: string | null;
  industry: string | null;
  created_at: string;
}

// Industry icon mapping
const INDUSTRY_ICONS: Record<string, any> = {
  'Retail': ShoppingCart,
  'Technology': Briefcase,
  'Finance': Landmark,
  'Banking': Landmark,
  'Manufacturing': Building2,
  'Industrial': Building2,
};

function getIconForProject(project: Project) {
  if (project.industry && INDUSTRY_ICONS[project.industry]) {
    return INDUSTRY_ICONS[project.industry];
  }
  return Folder;
}

interface ProjectSwitcherProps {
  activeProjectId: string;
  onProjectChange: (projectId: string) => void;
  onProjectSelect?: (project: { id: string; name: string }) => void;
  onNewProject?: () => void;
  className?: string;
}

export function ProjectSwitcher({
  activeProjectId,
  onProjectChange,
  onProjectSelect,
  onNewProject,
  className,
}: ProjectSwitcherProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [isManaging, setIsManaging] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get<{
        projects: Project[];
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
      }>('/api/projects/?status_filter=active&page_size=50');

      setProjects(response.projects);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
      console.error('Failed to load projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelected = (projectId: string) => {
    setSelectedProjectIds(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedProjectIds(new Set());
  };

  const deleteProjects = async (identifiers: string[]) => {
    if (identifiers.length === 0) return;

    const confirmed = window.confirm(
      identifiers.length === 1
        ? 'Delete this project? This permanently deletes the project and all proposals + conversation messages.'
        : `Delete ${identifiers.length} projects? This permanently deletes the projects and all proposals + conversation messages.`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    setError(null);

    try {
      if (identifiers.length === 1) {
        await apiClient.delete(`/api/projects/${encodeURIComponent(identifiers[0])}`);
      } else {
        await apiClient.post('/api/projects/batch-delete', { identifiers });
      }

      const deletedSet = new Set(identifiers);
      const remaining = projects.filter(p => !deletedSet.has(p.id));
      const activeWasDeleted = deletedSet.has(activeProjectId);

      clearSelection();
      setIsManaging(false);
      await loadProjects();

      if (activeWasDeleted) {
        if (remaining.length > 0) {
          onProjectChange(remaining[0].id);
        } else {
          onProjectChange('');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete projects');
      console.error('Failed to delete projects:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: 'var(--charcoal-light)' }}>
            Projects
          </h3>
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="w-full px-3 py-3 rounded-md border animate-pulse"
              style={{ background: 'var(--cream)', borderColor: 'var(--sand)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-md" style={{ background: 'var(--sand)' }} />
                <div className="flex-1 space-y-2">
                  <div className="h-4 rounded" style={{ background: 'var(--sand)', width: '70%' }} />
                  <div className="h-3 rounded" style={{ background: 'var(--sand)', width: '50%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('space-y-3', className)}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: 'var(--charcoal-light)' }}>
            Projects
          </h3>
        </div>
        <div className="p-4 rounded-md" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
          <p className="text-sm" style={{ color: '#991B1B' }}>{error}</p>
          <button
            onClick={loadProjects}
            className="text-xs mt-2 underline"
            style={{ color: '#991B1B' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <style>{`
        .project-item {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .project-item:hover {
          transform: translateX(2px);
        }

        .project-item.active {
          box-shadow: 0 2px 8px rgba(200, 88, 53, 0.15);
        }

        .project-icon {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .project-item.active .project-icon {
          transform: scale(1.05);
        }
      `}</style>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: 'var(--charcoal-light)' }}>
          Projects
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setIsManaging(prev => !prev);
              clearSelection();
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors"
            style={{
              color: isManaging ? 'white' : 'var(--charcoal-light)',
              background: isManaging ? 'var(--charcoal)' : 'transparent',
              border: 'none',
            }}
            disabled={isDeleting}
            onMouseEnter={(e) => {
              if (!isManaging) e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
            }}
            onMouseLeave={(e) => {
              if (!isManaging) e.currentTarget.style.background = 'transparent';
            }}
            title="Manage projects"
          >
            <Settings2 className="w-3 h-3" />
            Manage
          </button>

          {onNewProject && (
            <button
              onClick={onNewProject}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors"
              style={{
                color: 'var(--terracotta)',
                background: 'transparent',
                border: 'none',
              }}
              disabled={isManaging || isDeleting}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(200, 88, 53, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Plus className="w-3 h-3" />
              New
            </button>
          )}
        </div>
      </div>

      {isManaging && (
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs" style={{ color: 'var(--charcoal-light)' }}>
            {selectedProjectIds.size > 0 ? `${selectedProjectIds.size} selected` : 'Select projects to delete'}
          </div>
          <button
            onClick={() => deleteProjects(Array.from(selectedProjectIds))}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors"
            style={{
              color: 'white',
              background: selectedProjectIds.size > 0 ? '#B91C1C' : 'rgba(185, 28, 28, 0.4)',
              border: 'none',
            }}
            disabled={selectedProjectIds.size === 0 || isDeleting}
            title="Delete selected projects"
          >
            <Trash2 className="w-3 h-3" />
            Delete
          </button>
        </div>
      )}

      <div className="space-y-3">
        {projects.length === 0 ? (
          <div className="p-4 text-center rounded-md" style={{ background: 'var(--cream)' }}>
            <p className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
              No active projects
            </p>
            {onNewProject && (
              <button
                onClick={onNewProject}
                className="text-xs mt-2"
                style={{ color: 'var(--terracotta)', textDecoration: 'underline' }}
              >
                Create your first project
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Group projects by client */}
            {(() => {
              // Group projects by client_id
              const projectsByClient = projects.reduce((acc, project) => {
                const clientKey = project.client_id || 'no-client';
                if (!acc[clientKey]) {
                  acc[clientKey] = {
                    clientId: project.client_id,
                    clientName: project.client_name || 'No Client',
                    industry: project.industry,
                    projects: [],
                  };
                }
                acc[clientKey].projects.push(project);
                return acc;
              }, {} as Record<string, { clientId: string; clientName: string; industry: string | null; projects: Project[] }>);

              const activeProject = projects.find(p => p.id === activeProjectId || p.legacy_project_id === activeProjectId);
              const activeClientId = activeProject?.client_id || '';

              return Object.entries(projectsByClient).map(([clientKey, { clientId, clientName, industry, projects: clientProjects }]) => {
                const isActiveClient = clientId === activeClientId;
                const isExpanded = expandedClients.has(clientId);
                const hasActiveProject = clientProjects.some(p => p.id === activeProjectId || p.legacy_project_id === activeProjectId);

                const toggleClient = () => {
                  setExpandedClients(prev => {
                    const next = new Set(prev);
                    if (next.has(clientId)) {
                      next.delete(clientId);
                    } else {
                      next.add(clientId);
                    }
                    return next;
                  });
                };

                return (
                  <div key={clientKey} className="space-y-1">
                    {/* Client Header */}
                    <button
                      onClick={toggleClient}
                      className="w-full flex items-center justify-between px-2 py-1.5 rounded transition-colors"
                      style={{
                        background: isActiveClient ? 'rgba(200, 88, 53, 0.05)' : 'transparent',
                        color: 'var(--charcoal)',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActiveClient) {
                          e.currentTarget.style.background = 'rgba(200, 88, 53, 0.03)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActiveClient) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="w-3 h-3" style={{ color: 'var(--charcoal-light)' }} />
                        ) : (
                          <ChevronUp className="w-3 h-3" style={{ color: 'var(--charcoal-light)' }} />
                        )}
                        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--charcoal-light)' }}>
                          {clientName}
                        </span>
                      </div>
                      <span className="text-xs" style={{ color: 'var(--charcoal-light)' }}>
                        {clientProjects.length}
                      </span>
                    </button>

                    {/* Projects under this client */}
                    {(isExpanded || hasActiveProject) && (
                      <div className="ml-2 space-y-1">
                        {clientProjects.map((project) => {
                          const Icon = getIconForProject(project);
                          const isActive = project.id === activeProjectId || project.legacy_project_id === activeProjectId;
                          const isSelected = selectedProjectIds.has(project.id);

                          // Always show active project, show others only if expanded
                          if (!isActive && !isExpanded) return null;

                          return (
                            <div
                              key={project.id}
                              className={cn('project-item w-full text-left px-3 py-2 rounded-md border', {
                                active: isActive,
                              })}
                              style={{
                                background: isActive ? 'rgba(200, 88, 53, 0.08)' : 'var(--warm-white)',
                                borderColor: isActive ? 'var(--terracotta)' : 'var(--sand)',
                              }}
                            >
                              <div className="flex items-center gap-3">
                                {isManaging ? (
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleSelected(project.id)}
                                    disabled={isDeleting}
                                  />
                                ) : null}

                                <button
                                  onClick={() => {
                                    if (isManaging) return;
                                    onProjectChange(project.id);
                                    onProjectSelect?.({ id: project.id, name: project.name });
                                  }}
                                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                                  style={{ background: 'transparent', border: 'none', padding: 0 }}
                                  disabled={isManaging}
                                >
                                  <div
                                    className="project-icon p-2 rounded-md flex items-center justify-center"
                                    style={{
                                      background: isActive ? 'var(--terracotta)' : 'var(--cream)',
                                      color: isActive ? 'white' : 'var(--charcoal-light)',
                                    }}
                                  >
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate" style={{ color: 'var(--charcoal)' }}>
                                      {project.name}
                                    </p>
                                    <p className="text-xs truncate" style={{ color: 'var(--charcoal-light)' }}>
                                      {project.industry || 'No industry'}
                                    </p>
                                  </div>
                                </button>

                                {isManaging ? (
                                  <button
                                    onClick={() => deleteProjects([project.id])}
                                    className="p-1 rounded"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#B91C1C',
                                    }}
                                    disabled={isDeleting}
                                    title="Delete project"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </>
        )}
      </div>
    </div>
  );
}
